import { inject, Injectable, signal } from '@angular/core';
import { LocalStorage } from './local-storage.service';
import { PermissionAction } from '../constants/role.constants';
import { RolePermission, RoleReference } from '../interfaces/sub-admin.interface';
import { normalizeRoleReference, pickRoleReferences } from '../utils/role-permission.utils';
import { RolesUpdatedEventPayload } from '../../core/socket/socket.model';

@Injectable({
  providedIn: 'root'
})
export class RoleAccessService {
  private readonly localStorageService = inject(LocalStorage);
  private readonly permissionStateVersionSignal = signal(0);

  readonly permissionStateVersion = this.permissionStateVersionSignal.asReadonly();

  /**
   * Checks if the user has sub-admin details stored in local storage.
   * A user with sub-admin details is considered a sub-admin.
   * @returns True if the user is a sub-admin, false otherwise.
   */
  isSubAdmin(): boolean {
    return !!this.localStorageService.getDataByKey('subAdminDetails');
  }

  /**
   * Retrieves the stored sub-admin details from local storage.
   * @returns The stored sub-admin details if found, null otherwise.
   * @template T The type of the sub-admin details to be returned.
   */
  getSubAdminDetails<T = any>(): T | null {
    return this.localStorageService.getDataByKey('subAdminDetails');
  }

  /**
   * Returns an array of the active role identifiers.
   * The array of active role identifiers is derived from the active permissions
   * stored in local storage. The active permissions are retrieved by calling the
   * getActivePermissions() method.
   * @returns An array of the active role identifiers.
   */
  getActiveRoleIds(): string[] {
    this.permissionStateVersionSignal();
    return Array.from(new Set(this.getActivePermissions().map(permission => permission.roleId)));
  }

  /**
   * Checks if the user has the given role.
   * @param roleId The identifier of the role to check for.
   * @returns True if the user has the given role, false otherwise.
   */
  hasRole(roleId: string): boolean {
    return this.getActiveRoleIds().includes(roleId);
  }

  /**
   * Checks if the user has the given permission action on a specific role and optional category.
   * @param roleId The identifier of the role to check for.
   * @param action The permission action to check for.
   * @param categoryId The optional identifier of the category to check for.
   * @returns True if the user has the given permission action, false otherwise.
   */
  hasAction(roleId: string, action: PermissionAction, categoryId?: string): boolean {
    if (!this.isSubAdmin()) return true;
    const permissions = this.getPermissionsByRoleId(roleId);
    if (permissions.length === 0) return false;

    return permissions.some(permission => {
      if (categoryId && permission.categoryId && permission.categoryId !== categoryId) {
        return false;
      }

      if (categoryId && Array.isArray(permission.categoryIds) && permission.categoryIds.length > 0) {
        if (!permission.categoryIds.includes(categoryId)) {
          return false;
        }
      }

      if (!Array.isArray(permission.actions) || permission.actions.length === 0) {
        return action === 'read';
      }

      return permission.actions.includes(action);
    });
  }

  /**
   * Checks if the user has access to a specific category of a specific role.
   * @param roleId The identifier of the role to check for.
   * @param categoryId The identifier of the category to check for.
   * @returns True if the user has access to the category, false otherwise.
   */
  canAccessCategory(roleId: string, categoryId: string): boolean {
    this.permissionStateVersionSignal();
    if (!this.isSubAdmin()) return true;
    const permissions = this.getPermissionsByRoleId(roleId);
    if (permissions.length === 0) return false;

    const hasBroadAccess = permissions.some(permission => !permission.categoryId && !(permission.categoryIds?.length || 0));
    if (hasBroadAccess) return true;

    const categoryScoped = permissions.filter(permission => permission.categoryId || (permission.categoryIds?.length || 0) > 0);
    if (categoryScoped.length === 0) return true;

    return categoryScoped.some(permission => {
      if (permission.categoryId) {
        return permission.categoryId === categoryId;
      }

      return Array.isArray(permission.categoryIds) && permission.categoryIds.includes(categoryId);
    });
  }

  /**
   * Retrieves all allowed category IDs for a specific role.
   * If the role has broad access (i.e. no category restrictions), an empty array is returned.
   * @param roleId The identifier of the role to retrieve the allowed category IDs for.
   * @returns An array of allowed category IDs for the role.
   */
  getAllowedCategoryIds(roleId: string): string[] {
    this.permissionStateVersionSignal();
    const permissions = this.getPermissionsByRoleId(roleId);
    if (permissions.some(permission => !permission.categoryId && !(permission.categoryIds?.length || 0))) {
      return [];
    }

    const categoryIds = new Set<string>();
    permissions.forEach(permission => {
      if (permission.categoryId) {
        categoryIds.add(permission.categoryId);
      }

      permission.categoryIds?.forEach(categoryId => categoryIds.add(categoryId));
    });

    return Array.from(categoryIds);
  }

  /**
   * Retrieves all permissions for a specific role.
   * The permissions are retrieved from the active permissions stored in local storage.
   * @param roleId The identifier of the role to retrieve the permissions for.
   * @returns An array of permissions for the role.
   */
  getPermissionsForRole(roleId: string): RolePermission[] {
    this.permissionStateVersionSignal();
    return this.getPermissionsByRoleId(roleId);
  }

  /**
   * Checks if the user can perform a specific permission action on a specific role and optional category.
   * If the user is not a sub-admin, this method will return true.
   * @param roleId The identifier of the role to check for.
   * @param action The permission action to check for.
   * @param categoryId The optional identifier of the category to check for.
   * @returns True if the user can perform the permission action, false otherwise.
   */
  canPerform(roleId: string, action: PermissionAction, categoryId?: string): boolean {
    this.permissionStateVersionSignal();
    if (!this.isSubAdmin()) return true;
    return this.hasAction(roleId, action, categoryId);
  }

  /**
   * Retrieves the current sub-admin ID from local storage.
   * If the current sub-admin ID is not found in local storage, this method will return null.
   * @returns The current sub-admin ID or null if not found.
   */
  getCurrentSubAdminId(): string | null {
    const details = this.getSubAdminDetails<any>();
    return details?._id || details?.id || null;
  }

  /**
   * Applies the roles updated event to the current sub-admin details stored in local storage.
   * If the event payload contains a sub-admin ID that does not match the current sub-admin ID, or an email address that does not match the current sub-admin email address, this method will return false.
   * If the event payload contains valid updates for the current sub-admin, this method will apply the updates to local storage and return true.
   * @param payload - The roles updated event payload.
   * @returns True if the event payload was successfully applied, false otherwise.
   */
  applyRolesUpdatedEvent(payload: RolesUpdatedEventPayload): boolean {
    if (!this.isSubAdmin()) return false;

    const current = this.getSubAdminDetails<any>();
    if (!current || typeof current !== 'object') return false;

    const payloadUser = this.extractPayloadUser(payload);
    const targetSubAdminId = this.extractStringIdentifier(
      payload.subAdminId
      ?? payload.userId
      ?? payload.id
      ?? payloadUser?.['_id']
      ?? payloadUser?.['id']
    );
    const currentId = current._id || current.id;
    const currentEmail = typeof current.email === 'string' ? current.email.toLowerCase() : '';
    const targetEmail = typeof payloadUser?.['email'] === 'string' ? payloadUser['email'].toLowerCase() : '';

    if (targetSubAdminId && currentId && targetSubAdminId !== currentId) {
      return false;
    }
    if (!targetSubAdminId && targetEmail && currentEmail && targetEmail !== currentEmail) {
      return false;
    }

    const next = {
      ...current,
      ...(payloadUser || {}),
      ...(Array.isArray(payload.roles) ? { roles: payload.roles } : {}),
      ...(Array.isArray(payload.delegatedRoles) ? { delegatedRoles: payload.delegatedRoles } : {}),
      ...(payload.permissions ? { permissions: payload.permissions } : {}),
      ...(payload.status ? { status: payload.status } : {})
    };

    this.localStorageService.storeData('subAdminDetails', next);
    this.permissionStateVersionSignal.update(value => value + 1);
    return true;
  }

  /**
   * Retrieves the user object from the roles updated event payload.
   * The user object can be nested under the 'data' property or directly under the payload.
   * If the user object is not found, this method will return null.
   * @param payload - The roles updated event payload.
   * @returns The user object if found, or null if not found.
   */
  private extractPayloadUser(payload: RolesUpdatedEventPayload): Record<string, unknown> | null {
    if (payload?.user && typeof payload.user === 'object') {
      return payload.user as Record<string, unknown>;
    }

    const nestedUser = payload?.data?.user;
    if (nestedUser && typeof nestedUser === 'object') {
      return nestedUser as Record<string, unknown>;
    }

    return null;
  }

/**
 * Returns the given value as a string if it is a non-empty string, otherwise it returns undefined.
 * @param value - The value to be checked.
 * @returns The given value as a string if it is non-empty, otherwise undefined.
 */
  private extractStringIdentifier(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
  }

  /**
   * Returns an array of active permissions for the current sub-admin.
   * A permission is considered active if its status is not 'revoked', 'inactive', or 'pending'.
   * Each active permission is an object with the following properties:
   *  - roleId: The id of the role.
   *  - categoryId: The id of the category, if the permission is category-scoped.
   *    If the permission is not category-scoped, this property will be undefined.
   *  - actions: An array of the actions that are allowed for the permission.
   * @returns An array of active permissions for the current sub-admin.
   */
  private getActivePermissions(): RolePermission[] {
    const details = this.getSubAdminDetails<any>();
    if (!details) return [];

    const rawRoles = pickRoleReferences<RoleReference>(details);

    const activePermissions: RolePermission[] = [];

    rawRoles.forEach(role => {
      const normalizedRole = normalizeRoleReference(role);
      const { roleId, actions, categoryIds, status } = normalizedRole;
      if (!roleId) return;
      if (status === 'revoked' || status === 'inactive' || status === 'pending') return;

      if (categoryIds.length > 0) {
        categoryIds.forEach(categoryId => {
          activePermissions.push({
            roleId,
            categoryId,
            actions
          });
        });
        return;
      }

      activePermissions.push({
        roleId,
        actions
      });
    });

    return activePermissions;
  }

  /**
   * Retrieves all active permissions for a specific role.
   * @param roleId The identifier of the role to retrieve the permissions for.
   * @returns An array of active permissions for the role.
   */
  private getPermissionsByRoleId(roleId: string): RolePermission[] {
    return this.getActivePermissions().filter(permission => permission.roleId === roleId);
  }
}
